import React from 'react';
import {ScrollView, StyleSheet, Text, View} from 'react-native';
import {storiesOf} from '@storybook/react-native';

import {
  Code,
  FlexView,
  Footprint,
  Heading,
  Spacer,
  Spacing,
} from '/component/base/Storybook';
import {BUSINESS_COACH, BUSINESS_LEADER, COACH} from '/enum/competency.enum';
import {orangeLight} from '/theme/color/secondary.color';

import ProgressBar from '../ProgressBar';

const overrideStyle = StyleSheet.create({
  progressBar: {
    backgroundColor: orangeLight,
  },
  progressBarWrapper: {
    width: 50,
  },
});

storiesOf('base', module).add('ProgressBar', () => (
  <ScrollView>
    <Spacing size={20}>
      <FlexView>
        <Heading level={1}>Progress Bar</Heading>
        <Spacer size={16} />
        <Text>
          The <Code>ProgressBar</Code> component is used to render a progress
          bar.
        </Text>
        <Spacer size={16} />
        <Text>
          The "progress" on the progress bar is set via the{' '}
          <Code>progress</Code> prop passed into the component. This value is
          measured in percentage and therefore can not be below <Code>0</Code>{' '}
          nor exceed <Code>100</Code>.
        </Text>
        <Spacer size={16} />
        <Text>
          By default the current user's competency level will be used to
          determine the color of the "progress" component of the progress bar.
        </Text>
        <Spacer size={16} />
        <ProgressBar progress={15} />
        <Spacer size={6} />
        <Code>{'progress={15}'}</Code>
        <Spacer size={16} />
        <Heading level={2}>Overriding Competency Level</Heading>
        <Spacer size={16} />
        <Text>
          When the <Code>competency</Code> prop is set to a valid value it will
          override the current user's competency level and the corresponding
          color for the checkbox will be used instead.
        </Text>
        <Spacer size={16} />
        <ProgressBar progress={25} competency={COACH} />
        <Spacer size={6} />
        <Code>{'competency={COACH}'}</Code>
        <Spacer size={16} />
        <ProgressBar progress={35} competency={BUSINESS_COACH} />
        <Spacer size={6} />
        <Code>{'competency={BUSINESS_COACH}'}</Code>
        <Spacer size={16} />
        <ProgressBar progress={45} competency={BUSINESS_LEADER} />
        <Spacer size={6} />
        <Code>{'competency={BUSINESS_LEADER}'}</Code>
        <Spacer size={16} />
        <Heading level={2}>Overriding Specific Color</Heading>
        <Spacer size={16} />
        <Text>
          To render a <Code>ProgressBar</Code> in a specific color not
          correlated with any competency levels, the color can be set via the{' '}
          <Code>style</Code> prop's <Code>fill</Code> value.
        </Text>
        <Spacer size={16} />
        <ProgressBar progress={25} style={{fill: overrideStyle.progressBar}} />
        <Spacer size={6} />
        <Code>{'style={{fill: {backgroundColor: orangeLight}}}'}</Code>
        <Spacer size={16} />
        <Heading level={2}>Overriding Width</Heading>
        <Spacer size={16} />
        <Text>
          By default the <Code>ProgressBar</Code> will spen to the entire width
          of its parent. So to render a shorter <Code>ProgressBar</Code>, wrap
          it in a shorter parent component.
        </Text>
        <Spacer size={16} />
        <Footprint>
          <View style={overrideStyle.progressBarWrapper}>
            <ProgressBar progress={50} />
          </View>
        </Footprint>
        <Spacer size={16} />
        <Text>
          The above <Code>ProgressBar</Code> is wrapped in a parent{' '}
          <Code>View</Code> with <Code>width: 50</Code>.
        </Text>
        <Spacer size={16} />
        <Heading level={2}>Thin Variant</Heading>
        <Spacer size={16} />
        <Text>
          By default the <Code>ProgressBar</Code> component will render a thick
          progress bar variant. To change the variant, pass in the{' '}
          <Code>variant</Code> prop.
        </Text>
        <Spacer size={16} />
        <ProgressBar progress={25} variant="thin" />
        <Spacer size={6} />
        <Code>variant="thin"</Code>
      </FlexView>
    </Spacing>
  </ScrollView>
));
